/**
 * CashSessionDetailsModal — read-only detail view for a closed cash register.
 * Fetches /financiero/cash-registers/{id}/detail on open.
 * Does NOT alter the close flow; purely informational.
 */
import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Printer, Mail, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { printCaixaReportThermal } from "@/utils/printService";

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (v) => `€${(Number(v) || 0).toFixed(2)}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-PT"); } catch { return iso; }
};

const diffColor = (d) => {
  if (d === null || d === undefined) return "";
  if (Math.abs(d) < 0.01) return "text-emerald-700";
  if (Math.abs(d) < 5) return "text-amber-600";
  return "text-red-600";
};

// Build the reportData object expected by printCaixaReportThermal
function buildPrintData(caixa, restaurantName = "") {
  const resumo = caixa?.resumo || {};
  const conferencia = caixa?.conferencia_por_metodo || {};
  const rows = Object.entries(conferencia).map(([key, c]) => ({
    key,
    registado: c.registado ?? 0,
    recVal: c.recebido ?? null,
    diff: c.diferenca ?? null,
  }));
  const totalRegistado = rows.reduce((s, r) => s + (r.registado || 0), 0);
  return {
    restaurantName,
    date: caixa?.data || "",
    rows,
    totalRegistado,
    totalRecebido: caixa?.valor_contado ?? null,
    totalDiff: caixa?.diferenca ?? null,
    valorInicial: resumo.valor_inicial || 0,
    totalVendas: resumo.total_vendas || 0,
    cancelamentos: resumo.cancelamentos || 0,
  };
}

// ─── component ──────────────────────────────────────────────────────────────

export function CashSessionDetailsModal({ open, onClose, register, authAxios }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  // Fetch full detail (including db.caixa resumo) whenever register changes
  useEffect(() => {
    if (!open || !register?.id) return;
    setLoadingDetail(true);
    setDetail(null);
    authAxiosRef.current
      .get(`/financiero/cash-registers/${register.id}/detail`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail({ register, caixa: null }))
      .finally(() => setLoadingDetail(false));
  // register.id is enough — no need for the whole object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, register?.id]);

  const reg = detail?.register || register || {};
  const caixa = detail?.caixa;
  const resumo = caixa?.resumo || {};
  const conferencia = caixa?.conferencia_por_metodo || {};
  const hasResumo = caixa && Object.keys(resumo).length > 0;

  // ── actions ────────────────────────────────────────────────────────────────

  const handlePrint = async () => {
    if (!hasResumo) return;
    try {
      await printCaixaReportThermal(buildPrintData(caixa));
    } catch (err) {
      toast.error("Erro ao imprimir: " + (err?.message || "desconhecido"));
    }
  };

  const handleResendEmail = async () => {
    const addr = emailInput.trim();
    if (!addr) { toast.error("Introduz um email válido"); return; }
    setSendingEmail(true);
    try {
      await authAxiosRef.current.post(
        `/financiero/cash-registers/${register.id}/resend-email`,
        { email: addr }
      );
      toast.success(`Resumo enviado para ${addr}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao enviar email";
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col gap-0 p-0">

        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            Resumo do Caixa
            {reg.id && (
              <span className="font-mono text-xs text-muted-foreground">
                #{reg.id.slice(-6).toUpperCase()}
              </span>
            )}
            <Badge className="ml-auto bg-gray-500/20 text-gray-500 text-xs">Fechado</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {loadingDetail && (
            <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">A carregar...</span>
            </div>
          )}

          {!loadingDetail && (
            <>
              {/* ── Dados do caixa ── */}
              <section className="rounded-lg border p-4 space-y-2 text-sm">
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                  Dados do Caixa
                </p>
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                  <span className="text-muted-foreground">Data</span>
                  <span className="font-medium">{caixa?.data || reg.opened_at?.slice(0, 10) || "—"}</span>

                  <span className="text-muted-foreground">Abertura</span>
                  <span className="font-medium">{fmtDate(reg.opened_at || caixa?.aberto_em)}</span>

                  <span className="text-muted-foreground">Fechamento</span>
                  <span className="font-medium">{fmtDate(reg.closed_at || caixa?.fechado_em)}</span>

                  <span className="text-muted-foreground">Valor inicial</span>
                  <span className="font-medium">{fmt(reg.opening_amount ?? caixa?.valor_inicial)}</span>

                  <span className="text-muted-foreground">Responsável abertura</span>
                  <span className="font-medium truncate">{reg.opened_by || caixa?.aberto_por || "—"}</span>

                  <span className="text-muted-foreground">Responsável fecho</span>
                  <span className="font-medium truncate">{reg.closed_by || caixa?.fechado_por || "—"}</span>
                </div>
              </section>

              {/* ── Resumo financeiro ── */}
              {hasResumo && (
                <section className="rounded-lg border p-4 space-y-1.5 text-sm">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                    Resumo Financeiro
                  </p>

                  <div className="flex justify-between font-bold text-emerald-700 border-b pb-1.5">
                    <span>Total Vendas</span>
                    <span>{fmt(resumo.total_vendas)}</span>
                  </div>

                  {Object.entries(resumo.metodos || {}).map(([m, v]) => (
                    <div key={m} className="flex justify-between text-muted-foreground">
                      <span>{m}</span>
                      <span>{fmt(v)}</span>
                    </div>
                  ))}

                  {(resumo.cancelamentos || 0) > 0 && (
                    <div className="flex justify-between text-red-500 text-xs border-t pt-1.5">
                      <span>Cancelamentos</span>
                      <span>{fmt(resumo.cancelamentos)}</span>
                    </div>
                  )}
                </section>
              )}

              {/* ── Conferência por método ── */}
              {Object.keys(conferencia).length > 0 && (
                <section className="rounded-lg border p-4 text-sm">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2">
                    Conferência por Método
                  </p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted-foreground border-b">
                        <th className="text-left pb-1 font-semibold">Método</th>
                        <th className="text-right pb-1 font-semibold">Registado</th>
                        <th className="text-right pb-1 font-semibold">Contado</th>
                        <th className="text-right pb-1 font-semibold">Diferença</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(conferencia).map(([m, c]) => {
                        const diff = c.diferenca ?? null;
                        return (
                          <tr key={m} className="border-b last:border-0">
                            <td className="py-1">{m}</td>
                            <td className="py-1 text-right">{fmt(c.registado)}</td>
                            <td className="py-1 text-right">{c.recebido != null ? fmt(c.recebido) : "—"}</td>
                            <td className={`py-1 text-right font-bold ${diffColor(diff)}`}>
                              {diff === null ? "—" : diff === 0 ? "€0,00" : diff > 0 ? `+${fmt(diff)}` : `-${fmt(Math.abs(diff))}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </section>
              )}

              {/* ── Diferença global ── */}
              {caixa?.diferenca !== undefined && caixa?.diferenca !== null && (
                <section className={`rounded-lg border p-4 flex justify-between items-center text-sm ${Math.abs(caixa.diferenca) < 0.01 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                  <span className="font-bold">Diferença de Caixa</span>
                  <span className={`font-black text-lg ${diffColor(caixa.diferenca)}`}>
                    {Math.abs(caixa.diferenca) < 0.01
                      ? "✓ Caixa confere"
                      : caixa.diferenca > 0
                      ? `+${fmt(caixa.diferenca)}`
                      : `-${fmt(Math.abs(caixa.diferenca))}`}
                  </span>
                </section>
              )}

              {/* ── Reenviar por email ── */}
              <section className="rounded-lg border p-4 space-y-2 text-sm">
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                  Reenviar Resumo por Email
                </p>
                {hasResumo ? (
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="email@exemplo.com"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleResendEmail()}
                      className="flex-1 h-8 text-xs"
                    />
                    <Button
                      size="sm"
                      className="h-8 text-xs gap-1 shrink-0"
                      onClick={handleResendEmail}
                      disabled={sendingEmail}
                    >
                      {sendingEmail
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Mail className="w-3 h-3" />}
                      Enviar
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-amber-600">
                    Resumo detalhado não disponível — este caixa pode ter sido fechado antes da funcionalidade estar activa.
                  </p>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer actions */}
        <DialogFooter className="px-5 py-3 border-t flex-row flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={handlePrint}
            disabled={!hasResumo || loadingDetail}
          >
            <Printer className="w-3.5 h-3.5" />
            Imprimir
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs text-muted-foreground"
            disabled
            title="Funcionalidade em desenvolvimento"
          >
            <FileText className="w-3.5 h-3.5" />
            PDF em breve
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs ml-auto"
            onClick={onClose}
          >
            <X className="w-3.5 h-3.5" />
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
